import React, { useCallback, useEffect, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Action, redirect } from 'redux-first-router';
import MACPageHeader from './components/macPageHeader';
import { restoreSwitchUserEmail, setSwitchUserEmail } from './duck/actions/configUser/actions';
import validateEmailAddress from 'util/email';
import Button from '@atlaskit/button/standard-button';
import { LoadingButton } from '@atlaskit/button';
import { ErrorMessage, Label } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import { styled } from 'styled-components';
import { Dimensions } from 'components/visuals';
import { FormattedMessage } from 'react-intl';
import SectionMessage from '@atlaskit/section-message';
import {
  selectGetCustomerByEmail,
  selectIsMemberOfGroup,
  selectSwitchedUser,
} from './duck/selectors';
import { getCurrentContactEmail } from 'modules/auth/selectors';
import { fetchGetCustomerByEmail } from './duck/actions/contact';
import Loader from 'components/common/Loader';
import { route } from './duck/actions/landing';
import { messages } from './messages';
import { State } from '../../model/State';

const SwitchUserForm = styled.form`
  margin: ${Dimensions.units(3)}px 0;
  width: ${Dimensions.units(60)}px;
`;

const StyledSubmitButton = styled(LoadingButton)`
  margin-top: ${Dimensions.unit}px;
`;

const ConfigUserPage = () => {
  const dispatch = useDispatch();
  const alternateUser = useSelector((state) => (state as State).location?.query?.alternateUser);
  const decodedAlternateUser = alternateUser ? decodeURIComponent(alternateUser) : '';
  const [inputEmail, setInputEmail] = useState(decodedAlternateUser || '');
  const [debouncedInputEmail, setDebouncedInputEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isNotExistingEmail, setIsNotExistingEmail] = useState(false);
  const [isRevertEmailButtonDisabled, setIsRevertEmailButtonDisabled] = useState(true);
  const [isAccessCallFetching, setIsAccessCallFetching] = useState(true);
  const currentUserEmail = useSelector(getCurrentContactEmail);
  const switchedUserEmail = useSelector(selectSwitchedUser);
  const customersByEmail = useSelector(selectGetCustomerByEmail);
  const isMemberOfGroup = useSelector(selectIsMemberOfGroup);

  useEffect(() => {
    if (isMemberOfGroup === false) {
      setIsAccessCallFetching(false);
      dispatch(redirect(route() as Action));
    }
    if (isMemberOfGroup === true) {
      setIsAccessCallFetching(false);
    }
  }, [isMemberOfGroup]);

  useEffect(() => {
    const debounceInputTimeoutId = setTimeout(() => {
      if (inputEmail && !!validateEmailAddress(inputEmail)) {
        setIsEmailValid(true);
        setDebouncedInputEmail(inputEmail);
      } else {
        setIsEmailValid(false);
      }
    }, 200);
    return () => clearTimeout(debounceInputTimeoutId);
  }, [inputEmail]);

  const handleEmailFieldUpdate = (e) => {
    setInputEmail(e.target.value);

    setIsNotExistingEmail(false);
  };

  useEffect(() => {
    if (currentUserEmail === switchedUserEmail) {
      setIsRevertEmailButtonDisabled(true);
    } else {
      setIsRevertEmailButtonDisabled(false);
    }
  }, [switchedUserEmail, currentUserEmail]);

  useEffect(() => {
    if (customersByEmail[debouncedInputEmail]) {
      const { result } = customersByEmail[debouncedInputEmail];
      if (result) {
        dispatch(setSwitchUserEmail(debouncedInputEmail));
        dispatch(redirect(route() as Action));
      } else if (!customersByEmail[debouncedInputEmail]?.inProgress) {
        setIsNotExistingEmail(true);
      }
    }
  }, [customersByEmail[debouncedInputEmail]]);

  const handleEmailSwitch = useCallback(() => {
    dispatch(fetchGetCustomerByEmail(debouncedInputEmail));
  }, [debouncedInputEmail]);

  const handleEmailRestore = () => {
    if (currentUserEmail) {
      dispatch(fetchGetCustomerByEmail(currentUserEmail));
    }
    dispatch(restoreSwitchUserEmail());
    dispatch(redirect(route() as Action));
  };

  return (
    <>
      <MACPageHeader />
      {isAccessCallFetching ? (
        <Loader />
      ) : (
        <>
          <SectionMessage>
            <FormattedMessage {...messages.switchUserBanner} />
          </SectionMessage>
          <SwitchUserForm
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleEmailSwitch();
            }}
          >
            <Label htmlFor="userEmail">
              <FormattedMessage {...messages.switchUserInputLabel} />
            </Label>
            <Textfield
              name="email"
              id="userEmail"
              onSubmit={handleEmailSwitch}
              onChange={handleEmailFieldUpdate}
              value={inputEmail}
              data-cy="mac-update-user-input"
              isInvalid={isNotExistingEmail}
            />
            {isNotExistingEmail && (
              <ErrorMessage>
                <FormattedMessage {...messages.switchUserNotExistingEmail} />
              </ErrorMessage>
            )}
            <StyledSubmitButton
              type="submit"
              appearance="primary"
              isDisabled={!isEmailValid}
              data-cy="mac-update-user-button"
              isLoading={customersByEmail[debouncedInputEmail]?.inProgress}
            >
              <FormattedMessage {...messages.switchUserButtonUpdate} />
            </StyledSubmitButton>
          </SwitchUserForm>
          <Button
            type="button"
            onClick={handleEmailRestore}
            isDisabled={isRevertEmailButtonDisabled}
            data-cy="mac-revert-user-button"
          >
            <FormattedMessage {...messages.switchUserButtonRevert} />
          </Button>
        </>
      )}
    </>
  );
};

export const MACConfigUserPage = connect(null, null)(ConfigUserPage);
